import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BulkJobProgressPayload,
  CallAnalysisStatus,
  CallAnalysisExecutiveSynthesis,
  DataQuality,
} from '@marnic/shared';
import { formatDuration, formatUKForDisplay } from '@marnic/shared';
import { api } from '../../lib/api';
import { useSocket } from '../../lib/socket';
import { extractApiErrorMessage } from '../../lib/api-error';
import { labelForSkipReason } from '../../calls/skip-labels';
import { RiskBadge } from '../../calls/analysis/primitives/RiskBadge';

type Tab = 'active' | 'recent';

interface BulkJobItem {
  id: string;
  userId: string;
  mode: 'analyze_only' | 'analyze_with_re_stt';
  total: number;
  enqueued: number;
  succeeded: number;
  failed: number;
  skipped: number;
  cancelled: boolean;
  startedAt: string;
  finishedAt: string | null;
  errors: Array<{ callId: string; message: string }>;
  processSessionId: string;
}

interface BulkJobsResponse {
  processSessionId: string;
  items: BulkJobItem[];
}

interface RecentAnalysisItem {
  id: string;
  callId: string;
  status: CallAnalysisStatus;
  summary: string | null;
  suggestedDisposition: string | null;
  lastError: string | null;
  createdAt: string;
  completedAt: string | null;
  costMicros: number | null;
  phoneNumber: string;
  callStartedAt?: string | null;
  callDurationSeconds: number | null;
  // Pass 4 emits dataQuality on the executiveSynthesis JSON. Surfaced here
  // so admins can scan for low-value (fragmentary) analyses.
  executiveSynthesis: CallAnalysisExecutiveSynthesis | null;
}

interface RecentAnalysesResponse {
  total: number;
  items: RecentAnalysisItem[];
}

function progressPct(job: BulkJobItem): number {
  if (job.total === 0) return 100;
  return Math.round(((job.succeeded + job.failed + job.skipped) / job.total) * 100);
}

function formatCost(micros: number | null): string {
  if (micros === null) return '—';
  return `$${(micros / 1_000_000).toFixed(4)}`;
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
}

const DATA_QUALITY_VARIANT: Record<DataQuality, 'low' | 'medium' | 'high'> = {
  // RiskBadge styles low=sky, medium=amber, high=red. Map quality so
  // fragmentary trips the red badge (the row probably shouldn't have run).
  fragmentary: 'high',
  partial: 'medium',
  complete: 'low',
};

export function AiJobsSection(): JSX.Element {
  const [tab, setTab] = useState<Tab>('active');
  const [statusFilter, setStatusFilter] = useState<CallAnalysisStatus | 'all'>('all');
  const [hideSkipped, setHideSkipped] = useState(false);
  const [qualityFilter, setQualityFilter] = useState<'all' | DataQuality>('all');
  const qc = useQueryClient();
  const socket = useSocket();
  const [serverRestartedDuringJob, setServerRestartedDuringJob] = useState(false);
  const [cancelMsg, setCancelMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const jobsQuery = useQuery({
    queryKey: ['ai-bulk-jobs'],
    queryFn: () => api.get<BulkJobsResponse>('/ai/bulk-jobs'),
    refetchInterval: 5_000,
  });

  const recentQuery = useQuery({
    queryKey: ['call-analysis-recent', statusFilter],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '2000' });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      return api.get<RecentAnalysesResponse>(`/calls/analysis/recent?${params}`);
    },
    enabled: tab === 'recent',
  });

  // Detect server restart mid-job: any job's processSessionId differs from
  // the response's top-level processSessionId.
  useEffect(() => {
    if (!jobsQuery.data) return;
    const current = jobsQuery.data.processSessionId;
    const interrupted = jobsQuery.data.items.some(
      (j) => j.processSessionId !== current && !j.finishedAt,
    );
    setServerRestartedDuringJob(interrupted);
  }, [jobsQuery.data]);

  // Subscribe to active job rooms and live-merge progress events into the
  // cached query data so the bars tick in real time.
  const activeIds = useMemo(
    () => (jobsQuery.data?.items ?? []).filter((j) => !j.finishedAt).map((j) => j.id),
    [jobsQuery.data],
  );

  useEffect(() => {
    if (!socket || activeIds.length === 0) return;
    for (const id of activeIds) socket.emit('subscribe_bulk_job', id);
    const onProgress = (p: BulkJobProgressPayload): void => {
      qc.setQueryData<BulkJobsResponse>(['ai-bulk-jobs'], (old) => {
        if (!old) return old;
        const items = old.items.map((j) =>
          j.id === p.jobId
            ? {
                ...j,
                total: p.total,
                enqueued: p.enqueued,
                succeeded: p.succeeded,
                failed: p.failed,
                skipped: p.skipped,
                cancelled: p.cancelled,
                finishedAt: p.finishedAt,
                errors: p.recentErrors ?? j.errors,
              }
            : j,
        );
        return { ...old, items };
      });
    };
    socket.on('bulk_job_progress', onProgress);
    return () => {
      for (const id of activeIds) socket.emit('unsubscribe_bulk_job', id);
      socket.off('bulk_job_progress', onProgress);
    };
  }, [socket, activeIds, qc]);

  // Auto-invalidate recent analyses on each ready/failed event so the table
  // refreshes without polling.
  useEffect(() => {
    if (!socket) return;
    const refresh = (): void => {
      void qc.invalidateQueries({ queryKey: ['call-analysis-recent'] });
    };
    socket.on('call_analysis_ready', refresh);
    socket.on('call_analysis_failed', refresh);
    return () => {
      socket.off('call_analysis_ready', refresh);
      socket.off('call_analysis_failed', refresh);
    };
  }, [socket, qc]);

  const filteredItems = useMemo(() => {
    if (!recentQuery.data) return [];
    return recentQuery.data.items.filter((row) => {
      if (hideSkipped && row.status === 'skipped') return false;
      if (qualityFilter !== 'all') {
        const q = row.executiveSynthesis?.dataQuality;
        if (q !== qualityFilter) return false;
      }
      return true;
    });
  }, [recentQuery.data, hideSkipped, qualityFilter]);

  async function handleCancel(jobId: string): Promise<void> {
    setCancelMsg(null);
    try {
      const result = await api.post<{ skippedPending?: number }>(`/ai/bulk-jobs/${jobId}/cancel`);
      const skipped = result.skippedPending ?? 0;
      setCancelMsg({
        ok: true,
        text: `Cancellation requested. ${skipped} pending row${skipped === 1 ? '' : 's'} skipped; in-flight rows will finish.`,
      });
      void qc.invalidateQueries({ queryKey: ['ai-bulk-jobs'] });
      void qc.invalidateQueries({ queryKey: ['call-analysis-recent'] });
    } catch (err) {
      setCancelMsg({ ok: false, text: extractApiErrorMessage(err, 'Cancel failed.') });
    }
  }

  return (
    <section className="card p-4 space-y-3">
      <header className="border-b border-ink-200 pb-3 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-ink-900">AI jobs</h2>
          <p className="text-xs text-ink-500 mt-0.5">
            Live bulk-analyze jobs and a stream of recent per-call analyses.
          </p>
        </div>
        <div className="flex gap-1">
          <button
            type="button"
            className={`btn-secondary !text-xs ${tab === 'active' ? '!bg-ink-200' : ''}`}
            onClick={() => setTab('active')}
          >
            Active jobs
          </button>
          <button
            type="button"
            className={`btn-secondary !text-xs ${tab === 'recent' ? '!bg-ink-200' : ''}`}
            onClick={() => setTab('recent')}
          >
            Recent analyses
          </button>
        </div>
      </header>

      {serverRestartedDuringJob && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          One or more jobs were started under a previous server session. Their orchestrator state was lost on
          restart — re-run with the same filter to continue. Already-enqueued rows will still be processed by
          the worker.
        </div>
      )}

      {tab === 'active' && (
        <div className="space-y-2">
          {jobsQuery.isLoading && <p className="text-xs text-ink-400">Loading…</p>}
          {jobsQuery.data?.items.length === 0 && (
            <p className="text-xs text-ink-500 italic">No bulk jobs yet.</p>
          )}
          {jobsQuery.data?.items.map((job) => {
            const pct = progressPct(job);
            return (
              <div key={job.id} className="rounded border border-ink-200 bg-white p-2 space-y-1">
                <div className="flex items-center gap-2 text-xs flex-wrap">
                  <span className="font-mono text-[10px] text-ink-500">{job.id.slice(0, 8)}</span>
                  <span className="rounded bg-ink-100 px-1.5 py-0.5 text-[10px] uppercase">
                    {job.mode === 'analyze_with_re_stt' ? 're-STT' : 'analyze'}
                  </span>
                  {job.cancelled && <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] text-red-700">cancelled</span>}
                  {job.finishedAt ? (
                    <span className="text-emerald-700 font-medium">Finished</span>
                  ) : (
                    <span className="text-sky-700 font-medium">Running</span>
                  )}
                  <span className="ml-auto text-ink-500">
                    {job.enqueued}/{job.total} enqueued · {job.succeeded} ok · {job.failed} fail · {job.skipped} skip
                  </span>
                </div>
                <div className="h-2 rounded bg-ink-100 overflow-hidden">
                  <div
                    className={`h-full ${
                      job.cancelled ? 'bg-red-400' : pct === 100 ? 'bg-emerald-500' : 'bg-sky-500'
                    } transition-all`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="flex items-center gap-2 text-[10px] text-ink-500">
                  <span>started {new Date(job.startedAt).toLocaleString()}</span>
                  {job.finishedAt && <span>· finished {new Date(job.finishedAt).toLocaleString()}</span>}
                  {!job.finishedAt && (
                    <button
                      type="button"
                      className="ml-auto btn-secondary !text-[11px] !px-2 !py-0.5"
                      onClick={() => void handleCancel(job.id)}
                    >
                      Cancel
                    </button>
                  )}
                </div>
                {job.errors.length > 0 && (
                  <details>
                    <summary className="cursor-pointer text-[10px] text-red-600">
                      Errors ({job.errors.length})
                    </summary>
                    <ul className="mt-1 space-y-0.5 text-[10px] text-red-700">
                      {job.errors.slice(0, 8).map((e, idx) => (
                        <li key={idx} className="font-mono truncate">
                          {e.callId.slice(0, 8)}: {e.message}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            );
          })}
          {cancelMsg && (
            <p className={`text-xs ${cancelMsg.ok ? 'text-emerald-600' : 'text-red-600'}`}>
              {cancelMsg.text}
            </p>
          )}
        </div>
      )}

      {tab === 'recent' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <span className="text-ink-500">Status:</span>
            <select
              className="input !py-1"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="running">Running</option>
              <option value="succeeded">Succeeded</option>
              <option value="failed">Failed</option>
              <option value="skipped">Skipped</option>
            </select>
            <span className="text-ink-500 ml-2">Quality:</span>
            <select
              className="input !py-1"
              value={qualityFilter}
              onChange={(e) => setQualityFilter(e.target.value as typeof qualityFilter)}
            >
              <option value="all">All</option>
              <option value="complete">Complete</option>
              <option value="partial">Partial</option>
              <option value="fragmentary">Fragmentary</option>
            </select>
            <label className="flex items-center gap-1 ml-2">
              <input
                type="checkbox"
                checked={hideSkipped}
                onChange={(e) => setHideSkipped(e.target.checked)}
              />
              <span className="text-ink-700">Hide skipped</span>
            </label>
          </div>

          {recentQuery.data && (
            <p className="text-xs text-ink-400">
              Showing {filteredItems.length} of {recentQuery.data.total} analyses
            </p>
          )}
          {recentQuery.isLoading && <p className="text-xs text-ink-400">Loading…</p>}
          <div className="overflow-x-auto overflow-y-auto max-h-[65vh] rounded border border-ink-100">
            <table className="w-full text-xs">
              <thead className="text-left text-ink-500 border-b border-ink-100 sticky top-0 bg-white z-10">
                <tr>
                  <th className="font-normal py-1.5">Call</th>
                  <th className="font-normal py-1.5">Phone</th>
                  <th className="font-normal py-1.5">Length</th>
                  <th className="font-normal py-1.5">Status</th>
                  <th className="font-normal py-1.5">Quality</th>
                  <th className="font-normal py-1.5">Cost</th>
                  <th className="font-normal py-1.5">Completed</th>
                  <th className="font-normal py-1.5">Summary / error</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((row) => {
                    const dq = row.executiveSynthesis?.dataQuality;
                    const isSkip = row.status === 'skipped' && row.lastError;
                    return (
                      <tr key={row.id} className="border-b border-ink-50">
                        <td className="py-1.5">
                          <div className="font-mono text-[10px]">{row.callId.slice(0, 8)}</div>
                          <div className="mt-0.5 text-[10px] text-ink-500 tabular-nums">
                            Date of call: {formatDateTime(row.callStartedAt)}
                          </div>
                        </td>
                        <td className="py-1.5 font-mono text-[10px]">{formatUKForDisplay(row.phoneNumber)}</td>
                        <td className="py-1.5 font-mono text-[10px] tabular-nums">
                          {formatDuration(row.callDurationSeconds)}
                        </td>
                        <td className={`py-1.5 ${
                          row.status === 'failed' ? 'text-red-600'
                          : row.status === 'skipped' ? 'text-ink-500'
                          : row.status === 'succeeded' ? 'text-emerald-700'
                          : 'text-sky-700'
                        }`}>{row.status}</td>
                        <td className="py-1.5">
                          {dq ? <RiskBadge severity={DATA_QUALITY_VARIANT[dq]} label={dq} /> : <span className="text-ink-400">—</span>}
                        </td>
                        <td className="py-1.5 tabular-nums">{formatCost(row.costMicros)}</td>
                        <td className="py-1.5 text-ink-500">
                          {formatDateTime(row.completedAt)}
                        </td>
                        <td className="py-1.5 truncate max-w-md">
                          {isSkip ? (
                            <span className="text-ink-500 italic">{labelForSkipReason(row.lastError)}</span>
                          ) : row.lastError ? (
                            <span className="text-red-600">{row.lastError}</span>
                          ) : (
                            <span className="text-ink-700">{row.summary ?? ''}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
